import { BaseComponent } from '../../components/base-component'
import { $el, $list } from '../../utils/utils'
import Pikaday from 'pikaday'

const SELECT_TRIGGERS_SELECTOR = '.product-configurator__select-trigger'

/**
 * RentalStep Component
 *
 * Handles the rental configuration step where users select duration and date.
 * First step in the product configurator flow.
 */
export class RentalStep extends BaseComponent {
  /** Selected rental duration in days */
  private selectedDuration: number | null = null
  /** Selected rental start date */
  private selectedDate: Date | null = null
  /** Date picker instance */
  private datePicker: Pikaday | null = null
  /** Next step button element */
  private nextButton: HTMLButtonElement

  /**
   * Lifecycle callback when element is added to DOM
   * Initializes button reference and sets up event listeners
   */
  connectedCallback() {
    super.connectedCallback()
    this.nextButton = this.querySelector('.product-configurator__step-footer-button') as HTMLButtonElement
    this.initializeListeners()
  }

  /**
   * Sets up all event listeners for the step
   * - Duration selection
   * - Date picker triggers
   * - Document click for closing dropdowns
   */
  private initializeListeners() {
    // Duration select
    const durationItems = $list('.product-configurator__select--duration .product-configurator__select-list-item', this)
    durationItems.forEach(item => {
      this.addListener(item, 'click', this.handleDurationSelect.bind(this))
    })

    // Select triggers
    const selectTriggers = $list(SELECT_TRIGGERS_SELECTOR, this)
    selectTriggers.forEach(trigger => {
      this.addListener(trigger, 'click', this.handleSelectTriggerClick.bind(this))
    })

    // Document click for closing selects
    this.addListener(document, 'click', this.handleDocumentClick.bind(this))
  }

  /**
   * Handles clicks on select trigger elements
   * Opens/closes dropdowns and initializes date picker if needed
   * @param event Mouse event from trigger click
   */
  private handleSelectTriggerClick(event: MouseEvent) {
    event.stopPropagation()
    const trigger = (event.target as HTMLElement).closest(SELECT_TRIGGERS_SELECTOR) as HTMLElement
    const select = trigger.closest('.product-configurator__select') as HTMLElement

    // Close any other open selects first
    const activeSelects = $list('.product-configurator__select--active', this)
    activeSelects.forEach(activeSelect => {
      if (activeSelect !== select) {
        activeSelect.classList.remove('product-configurator__select--active')
      }
    })

    // Hide datepicker if opening a different select
    if (!select.classList.contains('product-configurator__select--date') && this.datePicker?.isVisible()) {
      this.datePicker.hide()
    }

    // Handle date picker select differently
    if (select.classList.contains('product-configurator__select--date')) {
      if (!this.datePicker) {
        this.initDatePicker(trigger)
        this.datePicker.el.classList.remove('is-hidden')
        this.datePicker.show()
      } else if (this.datePicker.isVisible()) {
        this.datePicker.hide()
      } else {
        this.datePicker.show()
      }
    } else {
      select.classList.toggle('product-configurator__select--active')
    }
  }

  /**
   * Handles selection of rental duration
   * Updates UI and validates step
   * @param event Mouse event from duration selection
   */
  private handleDurationSelect(event: MouseEvent) {
    const item = event.target as HTMLButtonElement
    const select = item.closest('.product-configurator__select')
    const trigger = $el('.product-configurator__select-trigger-text', select)

    this.selectedDuration = parseInt(item.dataset.value, 10)
    trigger.textContent = item.textContent.trim()
    select.classList.remove('product-configurator__select--active')

    this.updateValidity()
  }

  /**
   * Initializes the date picker component
   * Sets up callbacks and styling
   * @param triggerElement Element that triggers the date picker
   */
  private initDatePicker(triggerElement: HTMLElement) {
    const dateInput = $el('.product-configurator__select-trigger-text', triggerElement.parentElement)
    const select = triggerElement.closest('.product-configurator__select') as HTMLElement
    const selectWidth = select.offsetWidth

    // Get lead time and calculate minimum date
    const leadTime = parseInt(this.dataset.leadTime || '0', 10)
    const minDate = new Date()
    const blockSaturdays = this.dataset.blockSaturday === 'true'
    const blockSundays = this.dataset.blockSunday === 'true'

    // Calculate actual lead time accounting for blocked weekends
    let daysToAdd = leadTime
    let currentDate = new Date()
    while (daysToAdd > 0) {
      currentDate.setDate(currentDate.getDate() + 1)
      const dayOfWeek = currentDate.getDay()
      // Only count this day if it's not a blocked weekend day
      if (!(blockSaturdays && dayOfWeek === 6) && !(blockSundays && dayOfWeek === 0)) {
        daysToAdd--
      }
    }
    minDate.setTime(currentDate.getTime())

    // Parse blocked dates and weekend settings
    const blockedDatesStr = this.dataset.blockDates || ''
    const blockedDates = blockedDatesStr.split(',')
        .filter(date => date.trim())
        .map(date => new Date(date))

    this.datePicker = new Pikaday({
      field: dateInput as HTMLElement,
      minDate: minDate,
      disableDayFn: (date) => {
        const dayOfWeek = date.getDay()
        if (blockSaturdays && dayOfWeek === 6) return true
        if (blockSundays && dayOfWeek === 0) return true
        return blockedDates.some(
          (blockedDate) =>
            date.getFullYear() === blockedDate.getFullYear() &&
            date.getMonth() === blockedDate.getMonth() &&
            date.getDate() === blockedDate.getDate()
        )
      },
      toString(date: Date) {
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      },
      onSelect: (date: Date) => {
        this.selectedDate = date
        dateInput.textContent = this.datePicker.toString()
        select.classList.remove('product-configurator__select--active')
        this.datePicker.hide()
        this.updateValidity()
      },
      container: select,
      theme: 'product-configurator-theme',
      onDraw: () => {
        const calendar = this.datePicker.el
        calendar.style.width = `${selectWidth}px`
      },
      onOpen: () => {
        select.classList.add('product-configurator__select--active')
      },
      onClose: () => {
        select.classList.remove('product-configurator__select--active')
      }
    })
  }

  /**
   * Handles clicks outside of select elements
   * Closes any open dropdowns
   * @param event Mouse event from document click
   */
  private handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement

    if (!target.closest('.product-configurator__select')) {
      const activeSelects = $list('.product-configurator__select--active', this)
      activeSelects.forEach(select => {
        select.classList.remove('product-configurator__select--active')
      })

      if (this.datePicker?.isVisible()) {
        this.datePicker.hide()
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    if (this.datePicker) {
      this.datePicker.destroy()
    }
  }

  /**
   * Gets the selected rental duration
   * @returns number | null Selected duration in days or null if not selected
   */
  getDuration(): number | null {
    return this.selectedDuration
  }

  /**
   * Gets the selected rental start date
   * @returns Date | null Selected date or null if not selected
   */
  getDate(): Date | null {
    return this.selectedDate
  }

  /**
   * Gets the step data in a format that can be submitted
   * @returns Record<string, any> Step data object
   */
  getStepData(): Record<string, any> {
    return {
      duration: this.selectedDuration,
      date: this.selectedDate?.toISOString()
    }
  }

  /**
   * Checks if the step is valid (both duration and date are selected)
   * @returns boolean indicating if step can be completed
   */
  isValid(): boolean {
    return this.selectedDuration !== null && this.selectedDate !== null
  }

  /**
   * Updates the next button state based on validity
   * Enables/disables and updates styling
   */
  private updateValidity() {
    if (this.isValid()) {
      this.nextButton.classList.add('product-configurator__step-footer-button--active')
      this.nextButton.disabled = false
    } else {
      this.nextButton.classList.remove('product-configurator__step-footer-button--active')
      this.nextButton.disabled = true
    }
  }

  /**
   * Activates this step (makes it visible)
   */
  activate() {
    this.classList.add('product-configurator__step--active')
  }

  /**
   * Deactivates this step (hides it)
   */
  deactivate() {
    this.classList.remove('product-configurator__step--active')
  }

  /**
   * Marks this step as completed
   */
  complete() {
    this.classList.add('product-configurator__step--completed')
  }

  /**
   * Gets the step index from the data-index attribute
   * @returns number Index of this step in the configuration flow
   * @throws Error if data-index attribute is missing
   */
  getIndex(): number {
    const index = this.dataset.index
    if (!index) {
      console.error('RentalStep: Missing data-index attribute')
      return -1
    }
    return parseInt(index)
  }

  /**
   * Gets the selected rental start and end dates
   * @returns Object with startDate and endDate properties
   */
  getDates() {
    if (!this.selectedDate || !this.selectedDuration) {
      return { startDate: null, endDate: null }
    }

    const startDate = this.selectedDate
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + (this.selectedDuration - 1))

    // Format dates in YYYY-MM-DD format without timezone conversion
    const formatDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    }
  }

  /**
   * Gets the selected rental duration
   * @returns number Selected duration in days
   */
  getSelectedDuration(): number {
    return this.selectedDuration || 0
  }

  getRentalIntentToken(productId?: string): string {
    return ''; // Rental step doesn't have rental intents
  }

  reset() {
    // Reset duration and date selections
    this.selectedDuration = null
    this.selectedDate = null
    
    // Reset duration dropdown text
    const durationTrigger = this.querySelector('.product-configurator__select--duration .product-configurator__select-trigger-text')
    if (durationTrigger) {
        durationTrigger.textContent = 'Select duration'
    }
    
    // Reset date dropdown text
    const dateTrigger = this.querySelector('.product-configurator__select--date .product-configurator__select-trigger-text')
    if (dateTrigger) {
        dateTrigger.textContent = 'Select date'
    }
    
    // Reset all dropdowns to inactive state
    this.querySelectorAll('.product-configurator__select').forEach(select => {
        select.classList.remove('product-configurator__select--active')
    })
    
    // Reset next button
    const nextButton = this.querySelector('.product-configurator__step-footer-button') as HTMLButtonElement
    if (nextButton) {
        nextButton.classList.remove('product-configurator__step-footer-button--active')
        nextButton.disabled = true
    }
    
    // Hide datepicker if it's visible
    if (this.datePicker?.isVisible()) {
        this.datePicker.hide()
    }
  }
}